import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useApp } from './context';
import { toast } from 'sonner';
import DashboardShell from './DashboardShell';
import { Cpu, Plus, RefreshCw, Battery, Wifi, WifiOff, Activity, Zap } from './components/DeskeyIcons';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Skeleton } from './components/ui/skeleton';
import { Progress } from './components/ui/progress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDevices() {
  const { user, token } = useApp();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});
  const [createOpen, setCreateOpen] = useState(false);
  const [newDevice, setNewDevice] = useState({ name: '', serial_number: '' });

  const fetchDevices = async () => {
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const res = await axios.get(`${API}/devices`, { params: { tenant_id: user.tenant_id }, headers });
      setDevices(res.data);
    } catch { toast.error('Failed to load devices'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchDevices(); }, [user?.tenant_id]);

  const syncDevice = async (deviceId) => {
    const headers = { Authorization: `Bearer ${token}` };
    setSyncing(s => ({ ...s, [deviceId]: true }));
    try {
      await axios.post(`${API}/devices/${deviceId}/sync`, {}, { headers });
      toast.success('Device synced');
      fetchDevices();
    } catch { toast.error('Sync failed'); }
    finally { setSyncing(s => ({ ...s, [deviceId]: false })); }
  };

  const addDevice = async () => {
    if (!newDevice.name || !newDevice.serial_number) { toast.error('Name and serial number required'); return; }
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.post(`${API}/devices`, { ...newDevice, tenant_id: user.tenant_id }, { headers });
      toast.success('Device added');
      setCreateOpen(false);
      setNewDevice({ name: '', serial_number: '' });
      fetchDevices();
    } catch { toast.error('Failed to add device'); }
  };

  const ConnStatus = ({ status }) => {
    const colors = { online: '#10b981', offline: '#94a3b8', syncing: '#0ea5e9' };
    return (
      <div className="flex items-center gap-1.5">
        <span className={`pulse-dot ${status}`} style={{ width: 8, height: 8, borderRadius: '50%', background: colors[status] || '#94a3b8', display: 'inline-block' }}></span>
        <span className="text-xs capitalize" style={{ color: colors[status] }}>{status}</span>
      </div>
    );
  };

  return (
    <DashboardShell title="Device Inventory" breadcrumb="Admin Dashboard">
      <div className="space-y-4 fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex gap-3">
            <div className="kpi-card py-3 px-4">
              <p className="kpi-label">Total</p>
              <p className="kpi-number text-xl">{devices.length}</p>
            </div>
            <div className="kpi-card py-3 px-4">
              <p className="kpi-label">Online</p>
              <p className="kpi-number text-xl" style={{ color: '#10b981' }}>{devices.filter(d => d.connectivity_status === 'online').length}</p>
            </div>
            <div className="kpi-card py-3 px-4">
              <p className="kpi-label">Offline</p>
              <p className="kpi-number text-xl" style={{ color: '#94a3b8' }}>{devices.filter(d => d.connectivity_status === 'offline').length}</p>
            </div>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button style={{ background: 'hsl(var(--primary))', color: 'white', borderRadius: '0.75rem' }} data-testid="add-device-button">
                <Plus className="w-4 h-4 mr-2" /> Add Device
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-sm rounded-2xl">
              <DialogHeader><DialogTitle>Register Device</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-sm mb-1.5 block">Device Name</Label>
                  <Input placeholder="Classroom Recorder - Room 101" value={newDevice.name} onChange={e => setNewDevice(d => ({ ...d, name: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-sm mb-1.5 block">Serial Number</Label>
                  <Input placeholder="DSK-001-XXX" value={newDevice.serial_number} onChange={e => setNewDevice(d => ({ ...d, serial_number: e.target.value }))} className="font-mono" />
                </div>
                <Button onClick={addDevice} className="w-full" style={{ background: 'hsl(var(--primary))', color: 'white', borderRadius: '0.75rem' }}>Register Device</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Device Cards */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-48 rounded-2xl" />)}
          </div>
        ) : devices.length === 0 ? (
          <div className="empty-state">
            <Cpu className="empty-state-icon" />
            <p className="text-sm font-medium">No devices registered</p>
            <p className="text-xs mt-1">Add your first Desky recorder device</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {devices.map(d => (
              <div key={d.id} className="desky-card p-5">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-2"
                      style={{ background: 'hsl(var(--primary) / 0.1)' }}>
                      <Cpu className="w-5 h-5" style={{ color: 'hsl(var(--primary))' }} />
                    </div>
                    <p className="font-semibold text-sm">{d.name}</p>
                    <p className="text-xs font-mono mt-0.5" style={{ color: 'hsl(var(--muted-foreground))' }}>{d.serial_number}</p>
                  </div>
                  <ConnStatus status={d.connectivity_status} />
                </div>

                {/* Battery */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>Battery</span>
                    <span className="text-xs font-medium">{d.battery_level}%</span>
                  </div>
                  <Progress value={d.battery_level} className="h-1.5" />
                </div>

                {/* Info */}
                <div className="space-y-1 text-xs mb-4" style={{ color: 'hsl(var(--muted-foreground))' }}>
                  <p>Firmware: v{d.firmware_version}</p>
                  <p>Last sync: {d.last_sync_at ? new Date(d.last_sync_at).toLocaleString() : 'Never'}</p>
                </div>

                <Button variant="outline" size="sm" className="w-full text-xs" disabled={syncing[d.id]}
                  onClick={() => syncDevice(d.id)} data-testid={`sync-device-${d.id}`}>
                  {syncing[d.id] ? <><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Syncing...</> : <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Sync Now</>}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
